import {
  CalendarOutlined,
  CheckOutlined,
  ClockCircleOutlined,
  CloseOutlined,
  EditOutlined,
  EnvironmentOutlined
} from "@ant-design/icons"
import { Button, Divider, Flex, Popover, Skeleton, Tag, Typography } from "antd"
import dayjs from "dayjs"
import React, { type ReactNode, useState } from "react"
import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"

import { completeEvent } from "../../../Calendar/api/services.ts"
import { buildAddressMapUrl } from "../../../Calendar/CalendarPage/helpers/visit.ts"
import { useActivityEvent } from "./useActivityEvent.ts"

const { Text, Title, Paragraph } = Typography

type ActivityKind = "followUp" | "appointment"

const KIND_ICON: Record<ActivityKind, typeof ClockCircleOutlined> = {
  followUp: ClockCircleOutlined,
  appointment: CalendarOutlined
}
const KIND_ICON_COLOR: Record<ActivityKind, string> = {
  followUp: "#2563EB",
  appointment: "#2563EB"
}

interface ActivityQuickDetailsPopoverProps {
  kind: ActivityKind
  /** The linked calendar event's id — `lead.follow_up_event` / `appointment_event`. */
  eventId?: string | null
  overdue?: boolean
  /** Shown while the event loads, and as the whole content when there is no
   *  linked event yet (a naive follow-up date with no calendar sync). */
  fallbackDateLabel?: string | null
  /** Only ever shown when there is no linked event to fetch a real name from
   *  (see `hasLinkedEvent` below) — must be a resolved display name, never a
   *  raw id (a lead's `follow_up_owner` is a user id, not a name). */
  fallbackOwnerLabel?: string | null
  /** Deep link to the read-only Calendar view — the one navigation this
   *  popover still offers, for a viewer who has no `onEdit` at all (Edit
   *  itself never navigates; see `StandaloneEventEditModal`). */
  calendarHref?: string | null
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Omitted where the caller has no edit affordance to hand off to — the
   *  popover then shows details only, with no Edit action. */
  onEdit?: () => void
  /** Called after a follow-up is marked complete, so the caller can reload. */
  onCompleted?: () => void
  children: ReactNode
}

/** A subtle label over a prominent value — every section reads the same way. */
function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Flex vertical gap={2}>
      <Text type={"secondary"} className={"text-xs"}>
        {label}
      </Text>
      {children}
    </Flex>
  )
}

function formatEventTime(event: {
  start_at: string
  end_at?: string
  all_day?: boolean
}) {
  if (event.all_day) return null
  const start = dayjs(event.start_at).tz()
  if (!start.isValid()) return null
  const end = event.end_at ? dayjs(event.end_at).tz() : null
  return end?.isValid()
    ? `${start.format("h:mm A")}–${end.format("h:mm A")}`
    : start.format("h:mm A")
}

export function ActivityQuickDetailsPopover({
  kind,
  eventId,
  overdue,
  fallbackDateLabel,
  fallbackOwnerLabel,
  calendarHref,
  open,
  onOpenChange,
  onEdit,
  onCompleted,
  children
}: ActivityQuickDetailsPopoverProps) {
  const { t } = useTranslation()
  const { event, error, refetch } = useActivityEvent(eventId, open)
  const [completing, setCompleting] = useState(false)

  const close = () => onOpenChange(false)

  const handleEdit = () => {
    close()
    onEdit?.()
  }

  const handleComplete = async () => {
    if (!eventId) return
    setCompleting(true)
    try {
      const response = await completeEvent(eventId)
      if (response.ok) {
        close()
        onCompleted?.()
      } else {
        refetch()
      }
    } finally {
      setCompleting(false)
    }
  }

  const isCompleted = !!event?.is_completed
  const KindIcon = KIND_ICON[kind]

  // A linked event's own fields (date/time, participants) are authoritative
  // over the row's naive ones. The row only ever carries the owner's raw id,
  // never a resolved name, so nothing stands in for "Assigned To" while a
  // linked event is fetched — only the row's own date is trustworthy enough
  // to show early. Derived from `open`/`event`/`error` rather than the hook's
  // `loading` flag, which only flips true inside `useActivityEvent`'s effect
  // and so lags a render behind `open` — gating on it left one render where
  // the fallback date showed before the skeleton did.
  const hasLinkedEvent = !!eventId
  const pendingLinkedEvent = hasLinkedEvent && open && !event && !error

  const dateTimeLine = event
    ? [dayjs(event.start_at).tz().format("MMM D, YYYY"), formatEventTime(event)]
        .filter(Boolean)
        .join(" · ")
    : pendingLinkedEvent
      ? undefined
      : fallbackDateLabel
  const participants = event?.participants
    ?.map((p) => p?.full_name)
    .filter(Boolean)
    .join(", ")
  const notes = event?.comment as unknown as string | undefined
  const assignedTo =
    participants || (hasLinkedEvent ? undefined : fallbackOwnerLabel)

  const content = (
    <Flex
      vertical
      style={{ width: 288 }}
      data-testid={"lead-activity-quick-details"}
    >
      <Flex justify={"space-between"} align={"start"} gap={8}>
        <Flex gap={8} align={"center"}>
          <KindIcon style={{ fontSize: 16, color: KIND_ICON_COLOR[kind] }} />
          <Title level={5} className={"m-0"}>
            {t(`leadActivityQuickDetails.title.${kind}`)}
          </Title>
        </Flex>
        <Button
          type={"text"}
          size={"small"}
          className={"-mr-1.5 -mt-1"}
          icon={<CloseOutlined />}
          aria-label={t("leadActivityQuickDetails.actions.close")}
          onClick={close}
        />
      </Flex>

      {kind === "followUp" && (isCompleted || overdue) && (
        <Tag
          color={isCompleted ? "success" : "error"}
          className={"w-fit mt-1.5"}
        >
          {isCompleted
            ? t("leadActivityQuickDetails.status.completed")
            : t("leadActivityQuickDetails.status.overdue")}
        </Tag>
      )}

      <Divider className={"my-3"} />

      {/* A linked event is always fetched for its own fields, so while that
          is in flight there is nothing final to show yet — a naive fallback
          would just get replaced under the user a moment later. */}
      {pendingLinkedEvent && (
        <Skeleton active title={false} paragraph={{ rows: 3 }} />
      )}

      {/* Surfaced even when a fallback date still renders below — the fetch
          failing means participants/notes never arrived either, so staying
          silent here would read as "nothing else to show" rather than
          "couldn't load the rest". */}
      {error && !event && (
        <Text type={"secondary"} className={"text-xs"}>
          {t("leadActivityQuickDetails.loadFailed")}
        </Text>
      )}

      <Flex vertical gap={12}>
        {!!dateTimeLine && (
          <Field label={t("leadActivityQuickDetails.fields.dateTime")}>
            <Text
              className={overdue && !isCompleted ? "text-error" : undefined}
            >
              {dateTimeLine}
            </Text>
          </Field>
        )}

        {kind === "appointment" && !!event?.address && (
          <Field label={t("leadActivityQuickDetails.fields.location")}>
            <Flex vertical gap={0}>
              <Text>{event.address}</Text>
              <a
                href={buildAddressMapUrl(event.address)}
                rel={"noreferrer"}
                target={"_blank"}
                className={"text-xs inline-flex items-center gap-1 w-fit"}
              >
                <EnvironmentOutlined />
                {t("leadActivityQuickDetails.fields.openInMaps")}
              </a>
            </Flex>
          </Field>
        )}

        {!!assignedTo && (
          <Field
            label={t(
              kind === "followUp"
                ? "leadActivityQuickDetails.fields.assignedTo"
                : "leadActivityQuickDetails.fields.participants"
            )}
          >
            <Text>{assignedTo}</Text>
          </Field>
        )}

        {!!notes && (
          <Field label={t("leadActivityQuickDetails.fields.notes")}>
            <Paragraph
              className={"mb-0"}
              ellipsis={{ rows: 4, expandable: true, symbol: "more" }}
            >
              {notes}
            </Paragraph>
          </Field>
        )}
      </Flex>

      <Divider className={"my-3"} />

      <Flex gap={8} justify={"end"}>
        {!!onEdit && (
          <Button size={"small"} icon={<EditOutlined />} onClick={handleEdit}>
            {t("leadActivityQuickDetails.actions.edit")}
          </Button>
        )}

        {kind === "followUp" && !!eventId && !isCompleted && (
          <Button
            size={"small"}
            type={"primary"}
            icon={<CheckOutlined />}
            loading={completing}
            onClick={handleComplete}
          >
            {t("leadActivityQuickDetails.actions.complete")}
          </Button>
        )}

        {kind === "appointment" && !!calendarHref && (
          <Link to={calendarHref} onClick={close}>
            <Button size={"small"} type={"primary"}>
              {t("leadActivityQuickDetails.actions.openAppointment")}
            </Button>
          </Link>
        )}
      </Flex>
    </Flex>
  )

  return (
    <Popover
      trigger={"click"}
      open={open}
      onOpenChange={onOpenChange}
      content={content}
      placement={"bottomLeft"}
    >
      {children}
    </Popover>
  )
}
